import { resolvePlace } from "./resolvePlace";
import { clampText, MAX_NAME_LENGTH } from "./text";
import {
  ANY_ZONE,
  Place,
  PricingData,
  QuoteEndpoint,
  QuoteInput,
  QuoteLine,
  QuoteResult,
  Route,
  RouteEndpointKind,
  Tier,
  WithId,
} from "./types";

const round2 = (value: number) => Math.round(value * 100) / 100;

export const listPlaces = (data: PricingData): WithId<Place>[] =>
  Object.entries(data.places).map(([id, place]) => ({ id, ...place }));

const resolveEndpoint = (
  data: PricingData,
  placeId: string | undefined,
  text: string | undefined
): QuoteEndpoint | undefined => {
  const places = listPlaces(data);
  let place: WithId<Place> | undefined;

  if (placeId && data.places[placeId] && data.places[placeId].active) {
    place = { id: placeId, ...data.places[placeId] };
  } else if (text) {
    place = resolvePlace(text, places);
  }

  if (place) {
    const zone = data.zones[place.zoneId];
    return {
      placeId: place.id,
      name: place.name,
      zoneId: zone && zone.active ? place.zoneId : undefined,
      zoneName: zone?.name,
    };
  }

  const name = clampText(text, MAX_NAME_LENGTH);
  return name ? { name } : undefined;
};

export interface EndpointRef {
  placeId?: string;
  zoneId?: string;
}

const endpointKind = (zoneId: string, placeId?: string): RouteEndpointKind =>
  placeId ? "place" : zoneId === ANY_ZONE ? "any" : "zone";

/** Quão específica é uma ponta da tarifa: local > região > qualquer. */
const SPECIFICITY: Record<RouteEndpointKind, number> = { place: 4, zone: 2, any: 1 };

const endpointMatches = (zoneId: string, placeId: string | undefined, ref: EndpointRef | undefined) => {
  switch (endpointKind(zoneId, placeId)) {
    case "any":
      return true;
    case "zone":
      return !!ref?.zoneId && ref.zoneId === zoneId;
    case "place":
      return !!ref?.placeId && ref.placeId === placeId;
    default:
      return false;
  }
};

/**
 * Procura a tarifa ativa mais específica para o trajeto. Cada ponta pode ser
 * "qualquer", uma região ou um local; vence a combinação mais específica
 * (local→local > local→região > região→região > qualquer→região ...).
 * Tarifas bidirecionais também valem no sentido inverso; em empate, o sentido
 * direto vence.
 */
export function findRoute(
  routes: Record<string, Route>,
  origin: EndpointRef | undefined,
  destination: EndpointRef
): (WithId<Route> & { reversed: boolean }) | undefined {
  let best: (WithId<Route> & { reversed: boolean }) | undefined;
  let bestScore = -1;

  Object.entries(routes).forEach(([id, route]) => {
    if (!route.active || route.tiers.length === 0) return;
    const score =
      SPECIFICITY[endpointKind(route.originZoneId, route.originPlaceId)] +
      SPECIFICITY[endpointKind(route.destinationZoneId, route.destinationPlaceId)];

    const direct =
      endpointMatches(route.originZoneId, route.originPlaceId, origin) &&
      endpointMatches(route.destinationZoneId, route.destinationPlaceId, destination);
    const reversed =
      !direct &&
      route.bidirectional &&
      endpointMatches(route.originZoneId, route.originPlaceId, destination) &&
      endpointMatches(route.destinationZoneId, route.destinationPlaceId, origin);

    if (!direct && !reversed) return;
    // sentido direto desempata sobre o inverso
    const effective = score * 2 + (direct ? 1 : 0);
    if (effective > bestScore) {
      bestScore = effective;
      best = { id, ...route, reversed };
    }
  });

  return best;
}

export const findTier = (tiers: Tier[], passengers: number): Tier | undefined =>
  tiers.find((t) => passengers >= t.minPax && passengers <= t.maxPax);

/** Motor de cálculo puro. Nunca lança; devolve status "on_request" quando não há tabela. */
export function calculateQuote(input: QuoteInput, data: PricingData): QuoteResult {
  const currency = data.config.currency;
  const origin = resolveEndpoint(data, input.originPlaceId, input.originText);
  const destination = resolveEndpoint(data, input.destinationPlaceId, input.destinationText);

  const base: QuoteResult = {
    status: "on_request",
    currency,
    lines: [],
    total: 0,
    origin,
    destination,
  };

  const passengers = Math.floor(Number(input.passengers));
  if (!Number.isFinite(passengers) || passengers < 1) {
    return { ...base, reason: "invalid_passengers" };
  }

  if (!destination || !destination.zoneId) {
    return { ...base, reason: "unknown_place" };
  }

  const route = findRoute(
    data.routes,
    origin ? { placeId: origin.placeId, zoneId: origin.zoneId } : undefined,
    { placeId: destination.placeId, zoneId: destination.zoneId }
  );
  if (!route) {
    return { ...base, reason: "no_route" };
  }

  const tier = findTier(route.tiers, passengers);
  if (!tier) {
    return { ...base, reason: "pax_out_of_range" };
  }

  const lines: QuoteLine[] = [
    {
      kind: "leg",
      label: `${origin?.name ?? ""} → ${destination.name}`.replace(/^ → /, ""),
      amount: round2(tier.price),
      meta: { minPax: tier.minPax, maxPax: tier.maxPax },
    },
  ];

  if (input.roundTrip && data.config.roundTrip.enabled) {
    const multiplier = data.config.roundTrip.multiplier;
    lines.push({
      kind: "roundTrip",
      label: "roundTrip",
      amount: round2(tier.price * (multiplier - 1)),
      meta: { multiplier },
    });
  }

  (input.extraIds || []).forEach((extraId) => {
    const extra = data.extras[extraId];
    if (!extra || !extra.active) return;
    const perPax = extra.type === "perPax";
    lines.push({
      kind: "extra",
      label: extra.name,
      amount: round2(perPax ? extra.price * passengers : extra.price),
      meta: { extraId, perPax, unitPrice: extra.price },
    });
  });

  const total = round2(lines.reduce((sum, line) => sum + line.amount, 0));

  return {
    ...base,
    status: "ok",
    lines,
    total,
    matchedRoute: {
      id: route.id,
      originZoneId: route.originZoneId,
      originPlaceId: route.originPlaceId,
      destinationZoneId: route.destinationZoneId,
      destinationPlaceId: route.destinationPlaceId,
      reversed: route.reversed,
      tier,
    },
  };
}
